import React, { useState } from 'react';
import { Filter, Clock, TrendingUp, Calendar, Sparkles, CheckCircle, Target } from 'lucide-react';
import { useMissions } from '../hooks/useMissions';
import MissionCard from './MissionCard';
import LoadingSpinner from './LoadingSpinner';

type MissionCategory = 'Energy' | 'Transport' | 'Waste' | 'Water' | 'Food' | 'Community' | 'Shopping';

export default function Missions() {
  const [timeFilter, setTimeFilter] = useState<'all' | 'daily' | 'weekly' | 'monthly'>('all');
  const [categoryFilter, setCategoryFilter] = useState<'all' | MissionCategory>('all');
  const [difficultyFilter, setDifficultyFilter] = useState<'all' | 'easy' | 'medium' | 'hard'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'coming_soon' | 'completed'>('all');

  const { missions, loading } = useMissions();

  if (loading) {
    return <LoadingSpinner />;
  }

  console.log('All missions:', missions.length, missions.map(m => ({ title: m.title, active: m.active, coming_soon: m.coming_soon, completed: m.completed })));

  const filteredMissions = missions.filter((mission) => {
    // Time frame filter
    if (timeFilter !== 'all' && mission.time_frame !== timeFilter) return false;
    
    // Category filter
    if (categoryFilter !== 'all' && mission.category !== categoryFilter) return false;
    
    // Difficulty filter
    if (difficultyFilter !== 'all' && mission.difficulty !== difficultyFilter) return false;
    
    // Status filter
    if (statusFilter === 'active' && (mission.completed || mission.coming_soon || mission.active === false)) return false;
    if (statusFilter === 'coming_soon' && !mission.coming_soon) return false;
    if (statusFilter === 'completed' && !mission.completed) return false;
    
    return true;
  });

  const categories: MissionCategory[] = ['Energy', 'Transport', 'Waste', 'Water', 'Food', 'Community', 'Shopping'];

  // Separate missions by status for better organization
  const activeMissions = missions.filter(m => !m.coming_soon && !m.completed && m.active !== false);
  const comingSoonMissions = missions.filter(m => m.coming_soon);
  const completedMissions = missions.filter(m => m.completed);

  console.log('Mission breakdown:', {
    total: missions.length,
    active: activeMissions.length,
    comingSoon: comingSoonMissions.length,
    completed: completedMissions.length,
    filtered: filteredMissions.length
  });

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Eco Missions</h2>
        <p className="text-gray-600">
          Choose from a variety of environmental challenges to make a positive impact
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Filter className="h-5 w-5 text-gray-600" />
          <h3 className="font-semibold text-gray-900">Filter Missions</h3>
        </div>
        
        <div className="grid gap-4 md:grid-cols-4">
          {/* Time Frame Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Time Frame</label>
            <select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value as any)}
              className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">All Time Frames</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as any)}
              className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">All Categories</option>
              {categories.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          {/* Difficulty Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty</label>
            <select
              value={difficultyFilter}
              onChange={(e) => setDifficultyFilter(e.target.value as any)}
              className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">All Difficulties</option>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">All Missions</option>
              <option value="active">Available Now</option>
              <option value="coming_soon">Coming Soon</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        {/* Active Filters Summary */}
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing {filteredMissions.length} of {missions.length} missions
          </p>
          
          {(timeFilter !== 'all' || categoryFilter !== 'all' || difficultyFilter !== 'all' || statusFilter !== 'all') && (
            <button
              onClick={() => {
                setTimeFilter('all');
                setCategoryFilter('all');
                setDifficultyFilter('all');
                setStatusFilter('all');
              }}
              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
            >
              Clear all filters
            </button>
          )}
        </div>
      </div>

      {/* Mission Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="bg-emerald-100 p-2 rounded-lg">
              <Target className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{activeMissions.length}</p>
              <p className="text-sm text-gray-600">Available Now</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-100 p-2 rounded-lg">
              <TrendingUp className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {activeMissions.filter(m => m.trending).length}
              </p>
              <p className="text-sm text-gray-600">Trending Now</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="bg-pink-100 p-2 rounded-lg">
              <Sparkles className="h-6 w-6 text-pink-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {comingSoonMissions.length}
              </p>
              <p className="text-sm text-gray-600">Coming Soon</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="bg-green-100 p-2 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {completedMissions.length}
              </p>
              <p className="text-sm text-gray-600">Completed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Debug Info */}
      <div className="bg-gray-100 rounded-lg p-4 text-sm">
        <p><strong>Debug Info:</strong></p>
        <p>Total missions: {missions.length}</p>
        <p>Active missions: {activeMissions.length}</p>
        <p>Coming soon missions: {comingSoonMissions.length}</p>
        <p>Completed missions: {completedMissions.length}</p>
        <p>Filtered missions: {filteredMissions.length}</p>
        <p>Current status filter: {statusFilter}</p>
      </div>

      {/* Available Missions Section - FIRST ROW */}
      {activeMissions.length > 0 && (statusFilter === 'all' || statusFilter === 'active') && (
        <div>
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-emerald-500 p-2 rounded-lg">
              <Target className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900">Available Missions</h3>
              <p className="text-gray-600">Start making an impact today</p>
            </div>
            <div className="bg-emerald-100 px-3 py-1 rounded-full">
              <span className="text-emerald-700 font-medium text-sm">{activeMissions.length} available</span>
            </div>
          </div>
          
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {activeMissions.map((mission) => (
              <MissionCard key={mission.id} mission={mission} />
            ))}
          </div>
        </div>
      )}

      {/* Coming Soon Missions Section - SECOND ROW */}
      {comingSoonMissions.length > 0 && (statusFilter === 'all' || statusFilter === 'coming_soon') && (
        <div>
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-2 rounded-lg">
              <Calendar className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900">Coming Soon</h3>
              <p className="text-gray-600">Exciting new missions launching soon!</p>
            </div>
            <div className="bg-gradient-to-r from-purple-100 to-pink-100 px-3 py-1 rounded-full">
              <span className="text-purple-700 font-medium text-sm">{comingSoonMissions.length} missions</span>
            </div>
          </div>
          
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {comingSoonMissions.map((mission) => (
              <MissionCard key={mission.id} mission={mission} />
            ))}
          </div>
        </div>
      )}

      {/* Completed Missions Section - THIRD ROW */}
      {completedMissions.length > 0 && (statusFilter === 'all' || statusFilter === 'completed') && (
        <div>
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-green-500 p-2 rounded-lg">
              <CheckCircle className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900">Completed Missions</h3>
              <p className="text-gray-600">Your environmental achievements</p>
            </div>
            <div className="bg-green-100 px-3 py-1 rounded-full">
              <span className="text-green-700 font-medium text-sm">{completedMissions.length} completed</span>
            </div>
          </div>
          
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {completedMissions.map((mission) => (
              <MissionCard key={mission.id} mission={mission} />
            ))}
          </div>
        </div>
      )}

      {/* No Results */}
      {filteredMissions.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Filter className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No missions found</h3>
          <p className="text-gray-600 mb-4">
            Try adjusting your filters to see more missions
          </p>
          <button
            onClick={() => {
              setTimeFilter('all');
              setCategoryFilter('all');
              setDifficultyFilter('all');
              setStatusFilter('all');
            }}
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            Clear Filters
          </button>
        </div>
      )}
    </div>
  );
}